import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Sparkles, ChevronDown, Gift, Package, Timer,
  Users, Shield, Eye, ArrowRight, Star, Zap, CheckCircle2, Share2,
  Crown, Diamond, Send, Lock, TrendingUp, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { MagicCardFrame } from '../components/MagicCardFrame';
import { useApp } from '../App';

/* ─── Telegram SVG Logo ─── */
const TelegramIcon = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.28-.02-.12.03-2.07 1.32-5.84 3.87-.55.38-1.05.56-1.5.55-.49-.01-1.44-.28-2.15-.51-.87-.28-1.56-.43-1.5-.91.03-.25.38-.51 1.06-.78 4.15-1.81 6.93-3 8.32-3.6 3.97-1.65 4.8-1.94 5.33-1.95.12 0 .38.03.55.17.14.12.18.28.2.45-.01.06.01.24 0 .38z" fill="currentColor"/>
  </svg>
);

/* ─── Prize data ─── */
const PRIZE_BASICO = [
  { label: 'Ganador', amount: '$20,000' },
  { label: 'Nivel 1', amount: '$10,000' },
  { label: 'Nivel 2', amount: '$5,000' },
  { label: 'Nivel 3', amount: '$2,500' },
  { label: 'Nivel 4', amount: '$1,000' },
  { label: 'Nivel 5', amount: '$500' },
];
const PRIZE_DIAMANTE = [
  { label: 'Ganador', amount: '$30,000' },
  { label: 'Nivel 1', amount: '$20,000' },
  { label: 'Nivel 2', amount: '$10,000' },
  { label: 'Nivel 3', amount: '$5,000' },
  { label: 'Nivel 4', amount: '$2,500' },
  { label: 'Nivel 5', amount: '$1,000' },
];

/* ─── Animated counter ─── */
const AnimatedNumber = ({ target, duration = 1500 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!target || started.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const animate = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{count.toLocaleString()}</span>;
};

/* ─── Gold divider ─── */
const GoldDivider = () => (
  <div className="w-full h-px bg-[linear-gradient(90deg,rgba(255,198,64,0)_0%,rgba(255,198,64,0.35)_20%,rgba(255,198,64,0.35)_80%,rgba(255,198,64,0)_100%)] mb-10" />
);

/* ─── Section wrapper ─── */
const Section = ({ children, className = '', id }) => (
  <section id={id} className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 ${className}`}>
    {children}
  </section>
);

const Home = () => {
  const { raffles, user } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const refCode = searchParams.get('ref');
  useEffect(() => { if (refCode) localStorage.setItem('cofres_referral_code', refCode); }, [refCode]);

  /* ─── Raffle stats ─── */
  const activeRaffle = useMemo(() => raffles?.[0] || null, [raffles]);
  const totalChests = activeRaffle?.total_chests || 5000;
  const soldChests = activeRaffle?.sold || 0;
  const availableChests = activeRaffle?.available || totalChests;
  const progressPct = activeRaffle?.percentage || 0;

  /* ─── Navigation helpers ─── */
  const go = (path) => navigate(user ? path : '/registro');
  const handleOpenChests = () => go('/cofres');
  const handleDiamondMode = () => go('/cofres');
  const handleMyNetwork = () => go('/mi-red');
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="relative z-10 min-h-screen" data-testid="home-page" style={{ backgroundImage: 'url(/images/background.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed' }}>

      {/* ══════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════ */}
      <section className="relative overflow-hidden min-h-[85vh] flex items-center" data-testid="hero-section">
        {/* Decorative glow */}
        <div className="absolute inset-0 bg-[radial-gradient(70%_60%_at_10%_20%,rgba(255,198,64,0.12)_0%,rgba(255,198,64,0.04)_35%,rgba(9,10,13,0)_70%)] pointer-events-none" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 relative w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">

            {/* Left: Copy */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(53,208,127,0.08)] border border-[rgba(53,208,127,0.25)]">
                <div className="w-2 h-2 rounded-full bg-[#35D07F] animate-pulse" />
                <span className="text-xs text-[#35D07F] font-medium tracking-wide">Sorteo activo ahora</span>
              </div>

              <h1 className="font-heading font-bold tracking-tight">
                <span className="text-4xl sm:text-5xl lg:text-6xl text-[#F7F2E7] block leading-[1.1]">
                  Abre un cofre.
                </span>
                <span className="text-4xl sm:text-5xl lg:text-6xl text-[#FFC640] block leading-[1.1] mt-1">
                  Descubre tu precio.
                </span>
              </h1>

              <p className="text-base sm:text-lg text-[#F7F2E7]/70 max-w-xl leading-relaxed">
                Cada cofre esconde el costo de tu boleto: desde <span className="text-[#35D07F] font-semibold">$0 (gratis)</span> hasta <span className="text-[#FFC640] font-semibold">$50.00</span>. Todos participan por premios de hasta <span className="text-[#F7F2E7] font-semibold">$30,000</span>.
              </p>

              {/* Quick trust badges */}
              <div className="flex flex-wrap gap-2">
                {[
                  { icon: <Gift size={14} />, text: '99 cofres gratis', color: '#35D07F' },
                  { icon: <Shield size={14} />, text: 'Sorteo transparente', color: '#FFC640' },
                  { icon: <TrendingUp size={14} />, text: 'Gana por tu red', color: '#4BA3FF' },
                ].map((b, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border" style={{ color: b.color, borderColor: `${b.color}33`, backgroundColor: `${b.color}0D` }}>
                    {b.icon} {b.text}
                  </span>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-3 pt-2">
                <Button onClick={handleOpenChests} data-testid="hero-open-chests-button" className="h-14 px-8 bg-[#FFC640] text-[#090A0D] font-heading font-bold text-base hover:bg-[#FFD36B] rounded-xl shadow-[0_10px_30px_rgba(255,198,64,0.3)] transition-colors transition-shadow hover:-translate-y-0.5">
                  <Sparkles size={20} className="mr-2" /> Abrir cofres
                </Button>
                <Button onClick={() => scrollTo('como-funciona')} variant="outline" data-testid="hero-how-it-works-button" className="h-14 px-6 bg-[#121521] border-white/[0.12] text-[#F7F2E7] hover:border-[rgba(255,198,64,0.35)] hover:bg-[#171B2A] rounded-xl transition-colors">
                  <ChevronDown size={18} className="mr-2" /> Ver cómo funciona
                </Button>
              </div>
            </div>

            {/* Right: Live stats card */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="relative w-full max-w-sm">
                <div className="absolute -inset-8 bg-[radial-gradient(circle,rgba(255,198,64,0.12)_0%,transparent_60%)] pointer-events-none blur-xl" />
                <MagicCardFrame variant="stat" data-testid="hero-stats-card">
                  <div className="text-center mb-5">
                    <p className="text-xs text-[#F7F2E7]/50 uppercase tracking-wider mb-1">Premio mayor</p>
                    <p className="text-4xl font-heading font-bold text-[#FFC640] tabular-nums">$30,000</p>
                    <p className="text-[10px] text-[#4BA3FF] mt-1">Modo DIAMANTE</p>
                  </div>

                  {/* Progress bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-[#F7F2E7]/50">Cofres vendidos</span>
                      <span className="text-[#FFC640] font-heading font-semibold tabular-nums">{progressPct}%</span>
                    </div>
                    <div className="h-2 bg-white/[0.06] rounded-full overflow-hidden">
                      <div className="h-full bg-[linear-gradient(90deg,#FFC640,#FFD36B)] rounded-full transition-all duration-1000" style={{ width: `${Math.max(progressPct, 2)}%` }} />
                    </div>
                  </div>

                  {/* Stats grid */}
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Total', value: totalChests, color: '#F7F2E7' },
                      { label: 'Vendidos', value: soldChests, color: '#FFC640' },
                      { label: 'Disponibles', value: availableChests, color: '#35D07F' },
                    ].map((s, i) => (
                      <div key={i} className="text-center py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.04]">
                        <p className="text-lg font-heading font-bold tabular-nums" style={{ color: s.color }}>
                          <AnimatedNumber target={s.value} />
                        </p>
                        <p className="text-[10px] text-[#F7F2E7]/40 mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Price range indicator */}
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <span className="px-2.5 py-1 rounded-lg bg-[rgba(53,208,127,0.1)] border border-[rgba(53,208,127,0.25)] text-[#35D07F] font-heading font-bold text-xs">$0</span>
                    <div className="flex-1 h-px bg-[linear-gradient(90deg,rgba(53,208,127,0.4),rgba(255,198,64,0.4))]" />
                    <span className="px-2.5 py-1 rounded-lg bg-[rgba(255,198,64,0.1)] border border-[rgba(255,198,64,0.25)] text-[#FFC640] font-heading font-bold text-xs">$50</span>
                  </div>
                  <p className="text-[10px] text-[#F7F2E7]/35 text-center mt-2">Rango de precios por cofre</p>
                </MagicCardFrame>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          WHAT IS THIS? (concise explainer)
      ══════════════════════════════════════════════ */}
      <Section>
        <MagicCardFrame variant="banner" data-testid="explainer-section">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-11 h-11 rounded-xl bg-[rgba(255,198,64,0.1)] flex items-center justify-center flex-shrink-0">
              <Eye size={22} className="text-[#FFC640]" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-base md:text-lg text-[#F7F2E7]">
                No compras un premio. Descubres el costo de tu boleto.
              </h2>
              <p className="text-sm text-[#F7F2E7]/55 mt-1">Cada cofre contiene un precio entre $1 y $50. El precio se revela al abrirlo.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 ml-0 sm:ml-[60px]">
            {[
              { icon: <Package size={16} />, text: '5,000 cofres numerados', sub: 'Precios de $1 a $50', color: '#FFC640' },
              { icon: <Gift size={16} />, text: '99 cofres gratis ($0)', sub: 'Mezclados al azar', color: '#35D07F' },
              { icon: <Star size={16} />, text: 'Todos en el mismo sorteo', sub: 'Sin importar el precio', color: '#4BA3FF' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2.5 p-3.5 rounded-xl border" style={{ borderColor: `${item.color}20`, backgroundColor: `${item.color}08` }}>
                <div className="mt-0.5 flex-shrink-0" style={{ color: item.color }}>{item.icon}</div>
                <div>
                  <p className="text-sm font-medium text-[#F7F2E7]/85">{item.text}</p>
                  <p className="text-xs text-[#F7F2E7]/45 mt-0.5">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </MagicCardFrame>
      </Section>

      {/* ══════════════════════════════════════════════
          HOW IT WORKS — 4 STEPS
      ══════════════════════════════════════════════ */}
      <Section id="como-funciona">
        <GoldDivider />
        <div className="text-center mb-10">
          <h2 className="font-heading font-bold text-base md:text-lg text-[#F7F2E7]">4 pasos para participar</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { step: '1', icon: <TelegramIcon size={26} className="text-[#229ED9]" />, title: 'Conecta Telegram', desc: 'Regístrate y vincula tu Telegram. Ahí recibirás confirmaciones y notificaciones.', color: '#229ED9' },
            { step: '2', icon: <Package size={26} className="text-[#FFC640]" />, title: 'Abre cofres', desc: 'Cada cofre revela el precio exacto de tu boleto. Elige los que quieras.', color: '#FFC640' },
            { step: '3', icon: <Sparkles size={26} className="text-[#4BA3FF]" />, title: 'Paga y confirma', desc: 'Envía tu comprobante por Telegram. El bot lo verifica automáticamente.', color: '#4BA3FF' },
            { step: '4', icon: <Award size={26} className="text-[#35D07F]" />, title: 'Participa y gana', desc: 'Todos los boletos entran al mismo sorteo. Con 3+ cofres activas Modo Diamante.', color: '#35D07F' },
          ].map((s) => (
            <MagicCardFrame key={s.step} variant="small" hover={true} data-testid={`step-${s.step}`}>
              <div className="relative">
                <div className="absolute top-0 right-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-heading font-bold" style={{ backgroundColor: `${s.color}18`, color: s.color }}>
                  {s.step}
                </div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${s.color}12` }}>
                  {s.icon}
                </div>
                <h3 className="font-heading font-semibold text-[#F7F2E7] text-sm mb-1.5">{s.title}</h3>
                <p className="text-xs text-[#F7F2E7]/55 leading-relaxed">{s.desc}</p>
              </div>
            </MagicCardFrame>
          ))}
        </div>

        <div className="flex items-center justify-center gap-2 mt-6 px-4 py-2 rounded-xl bg-[rgba(34,158,217,0.06)] border border-[rgba(34,158,217,0.12)] w-fit mx-auto">
          <TelegramIcon size={14} className="text-[#229ED9]" />
          <span className="text-xs text-[#229ED9]">Necesitas Telegram para participar</span>
        </div>
      </Section>

      {/* ── Mid CTA ── */}
      <div className="flex flex-wrap justify-center gap-3 py-6">
        <Button onClick={handleOpenChests} data-testid="cta-mid-open-chests" className="h-12 px-8 bg-[#FFC640] text-[#090A0D] font-heading font-semibold hover:bg-[#FFD36B] rounded-xl shadow-[0_10px_30px_rgba(255,198,64,0.3)] transition-colors transition-shadow">
          <Sparkles size={18} className="mr-2" /> Abrir cofres
        </Button>
        <Button onClick={handleDiamondMode} variant="outline" data-testid="cta-mid-diamond" className="h-12 px-8 bg-[rgba(75,163,255,0.08)] border-[rgba(75,163,255,0.25)] text-[#4BA3FF] hover:bg-[rgba(75,163,255,0.15)] rounded-xl transition-colors">
          <Diamond size={18} className="mr-2" /> Modo DIAMANTE
        </Button>
      </div>

      {/* ══════════════════════════════════════════════
          PARTICIPATION MODES
      ══════════════════════════════════════════════ */}
      <Section id="modos">
        <GoldDivider />
        <div className="text-center mb-8">
          <h2 className="font-heading font-bold text-base md:text-lg text-[#F7F2E7]">Modos de Participación</h2>
          <p className="text-sm text-[#F7F2E7]/50 mt-2">
            Con <span className="text-[#4BA3FF] font-medium">3 cofres</span> activas <span className="text-[#4BA3FF] font-medium">MODO DIAMANTE</span> y multiplicas tus premios.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {/* BASICO */}
          <MagicCardFrame variant="medium" data-testid="mode-basico">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[rgba(255,198,64,0.1)] flex items-center justify-center">
                <Package size={20} className="text-[#FFC640]" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-base text-[#FFC640]">BASICO</h3>
                <p className="text-[11px] text-[#F7F2E7]/45">1 o 2 cofres</p>
              </div>
            </div>
            <div className="space-y-1.5">
              {PRIZE_BASICO.map((p) => (
                <div key={p.label} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-white/[0.02]">
                  <span className="text-[#F7F2E7]/60 text-sm">{p.label}</span>
                  <span className="font-heading font-bold text-sm text-[#FFC640] tabular-nums">{p.amount}</span>
                </div>
              ))}
            </div>
          </MagicCardFrame>

          {/* DIAMANTE */}
          <MagicCardFrame variant="medium" data-testid="mode-diamante">
            <div className="relative">
              <div className="absolute top-0 right-0">
                <span className="px-2.5 py-0.5 text-[10px] font-heading font-bold bg-[rgba(75,163,255,0.15)] text-[#4BA3FF] rounded-full border border-[rgba(75,163,255,0.3)]">RECOMENDADO</span>
              </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[rgba(75,163,255,0.12)] flex items-center justify-center">
                <Diamond size={20} className="text-[#4BA3FF]" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-base text-[#4BA3FF]">DIAMANTE</h3>
                <p className="text-[11px] text-[#F7F2E7]/45">3 cofres o mas</p>
              </div>
            </div>
            <div className="space-y-1.5">
              {PRIZE_DIAMANTE.map((p, i) => (
                <div key={p.label} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-[rgba(75,163,255,0.04)]">
                  <span className="text-[#F7F2E7]/60 text-sm">{p.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-heading font-bold text-sm text-[#4BA3FF] tabular-nums">{p.amount}</span>
                    {i === 0 && <span className="text-[8px] text-[#35D07F] font-bold">+$10K</span>}
                  </div>
                </div>
              ))}
            </div>
            </div>
          </MagicCardFrame>
        </div>

        <div className="text-center mt-5">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[rgba(53,208,127,0.08)] border border-[rgba(53,208,127,0.15)]">
            <Zap size={14} className="text-[#35D07F]" />
            <span className="text-xs text-[#35D07F] font-medium">DIAMANTE te da <span className="font-bold">+$29,500</span> en premios totales</span>
          </div>
        </div>

        <div className="flex justify-center mt-6">
          <Button onClick={handleDiamondMode} data-testid="cta-diamond-mode" className="h-12 px-8 bg-[rgba(75,163,255,0.15)] border border-[rgba(75,163,255,0.3)] text-[#4BA3FF] font-heading font-semibold hover:bg-[rgba(75,163,255,0.25)] rounded-xl transition-colors">
            <Diamond size={18} className="mr-2" /> Activar MODO DIAMANTE
          </Button>
        </div>
      </Section>

      {/* ══════════════════════════════════════════════
          REFERRAL + 99 FREE CHESTS (side by side)
      ══════════════════════════════════════════════ */}
      <Section id="referidos">
        <GoldDivider />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">

          {/* Referidos */}
          <MagicCardFrame variant="medium" data-testid="referral-section">
            <div className="text-center">
              <h2 className="font-heading font-bold text-base md:text-lg text-[#F7F2E7] mb-2">Gana por tu red</h2>
              <p className="text-xs text-[#F7F2E7]/55 mb-1">Comparte tu codigo. Si alguien gana, <span className="text-[#35D07F] font-semibold">tu tambien cobras</span>.</p>
              <p className="text-xs text-[#F7F2E7]/55 mb-4">Hasta <span className="text-[#35D07F] font-semibold">5 niveles</span> de referidos.</p>

              {/* Visual chain compact */}
              <div className="flex items-center justify-center gap-1 mb-4 py-2">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-9 h-9 rounded-full bg-[rgba(255,198,64,0.12)] border-2 border-[#FFC640] flex items-center justify-center">
                    <Crown size={14} className="text-[#FFC640]" />
                  </div>
                  <span className="text-[9px] text-[#FFC640] mt-1 font-bold">TU</span>
                </div>
                <ArrowRight size={10} className="text-[#F7F2E7]/25 flex-shrink-0" />
                {[1, 2, 3, 4, 5].map((level) => (
                  <React.Fragment key={level}>
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="w-7 h-7 rounded-full bg-[rgba(53,208,127,0.08)] border border-[rgba(53,208,127,0.25)] flex items-center justify-center">
                        <span className="text-[8px] text-[#35D07F] font-bold">N{level}</span>
                      </div>
                      <span className="text-[8px] text-[#F7F2E7]/35 mt-0.5">Nv{level}</span>
                    </div>
                    {level < 5 && <ArrowRight size={8} className="text-[#F7F2E7]/15 flex-shrink-0" />}
                  </React.Fragment>
                ))}
              </div>

              <Button onClick={handleMyNetwork} data-testid="cta-my-network" className="h-10 px-5 bg-[#35D07F] text-[#090A0D] font-heading font-semibold text-xs hover:bg-[#45E08F] rounded-xl transition-colors">
                <Share2 size={14} className="mr-1.5" /> Ver mi red
              </Button>
            </div>
          </MagicCardFrame>

          {/* 99 Cofres Gratis */}
          <MagicCardFrame variant="medium" data-testid="free-chests-section">
            <div className="text-center">
              <h2 className="font-heading font-bold text-base md:text-lg text-[#F7F2E7] mb-2">99 cofres GRATIS</h2>
              <p className="text-xs text-[#F7F2E7]/55 mb-1">Mezclados al azar. Si encuentras uno:</p>
              <p className="text-xs text-[#35D07F] font-semibold mb-4">Participas sin pagar nada.</p>

              <div className="space-y-1.5 text-left">
                {[
                  'Valor $0.00 — totalmente gratis',
                  'Ubicados aleatoriamente',
                  'Participan igual que cualquier boleto',
                  'Puedes ganar el premio mayor',
                ].map((text, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(53,208,127,0.06)] border border-[rgba(53,208,127,0.1)]">
                    <Gift size={12} className="text-[#35D07F] flex-shrink-0" />
                    <span className="text-[11px] text-[#F7F2E7]/70">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </MagicCardFrame>

        </div>
      </Section>
      <Section>
        <GoldDivider />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Transparency */}
          <MagicCardFrame variant="medium" data-testid="transparency-section">
            <h2 className="font-heading font-bold text-base md:text-lg text-[#F7F2E7] mb-4 flex items-center gap-2">
              <Shield size={18} className="text-[#FFC640]" /> Transparente y justo
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: <Eye size={18} />, title: 'Sorteo en vivo', desc: 'Al vender todos los cofres' },
                { icon: <Package size={18} />, title: 'Boletos numerados', desc: 'Cada cofre = un numero' },
                { icon: <CheckCircle2 size={18} />, title: 'Reglas claras', desc: 'Mismas oportunidades' },
                { icon: <Lock size={18} />, title: 'Sistema seguro', desc: 'Sin manipulacion' },
              ].map((item, i) => (
                <div key={i} className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                  <div className="text-[#FFC640] mb-2">{item.icon}</div>
                  <h4 className="font-heading font-semibold text-[#F7F2E7] text-xs mb-0.5">{item.title}</h4>
                  <p className="text-[11px] text-[#F7F2E7]/45">{item.desc}</p>
                </div>
              ))}
            </div>
          </MagicCardFrame>

          {/* Why Telegram */}
          <MagicCardFrame variant="medium" data-testid="telegram-section">
            <h2 className="font-heading font-bold text-base md:text-lg text-[#F7F2E7] mb-4 flex items-center gap-2">
              <TelegramIcon size={18} className="text-[#229ED9]" /> Por que usamos Telegram
            </h2>
            <div className="space-y-2.5">
              {[
                { text: 'Registro seguro de usuarios', sub: 'Tu cuenta vinculada al bot' },
                { text: 'Confirmacion instantanea', sub: 'Notificaciones de compras en tiempo real' },
                { text: 'Envio de comprobantes', sub: 'Foto del pago y verificacion con IA' },
                { text: 'Soporte por chat', sub: 'Bot inteligente 24/7 + soporte humano' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-[rgba(34,158,217,0.04)] border border-[rgba(34,158,217,0.08)]">
                  <TelegramIcon size={14} className="text-[#229ED9] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-[#F7F2E7]/75 font-medium">{item.text}</p>
                    <p className="text-[11px] text-[#F7F2E7]/40 mt-0.5">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </MagicCardFrame>
        </div>
      </Section>

      {/* ══════════════════════════════════════════════
          FINAL CTA
      ══════════════════════════════════════════════ */}
      <Section className="pb-20 sm:pb-16">
        <MagicCardFrame variant="banner" data-testid="final-cta-section">
          <div className="text-center">
            <h2 className="font-heading font-bold text-base md:text-lg text-[#F7F2E7] mb-2">Listo para descubrir tu precio?</h2>
            <p className="text-sm text-[#F7F2E7]/50 mb-6 max-w-md mx-auto">
              Abre un cofre y participa por premios de hasta $30,000.
            </p>

            <div className="flex flex-wrap justify-center gap-3">
              <Button onClick={handleOpenChests} data-testid="cta-final-open-chests" className="h-14 px-10 bg-[#FFC640] text-[#090A0D] font-heading font-bold text-base hover:bg-[#FFD36B] rounded-xl shadow-[0_10px_40px_rgba(255,198,64,0.35)] transition-colors transition-shadow">
                <Sparkles size={20} className="mr-2" /> Abrir cofres ahora
              </Button>
              <Button onClick={handleDiamondMode} variant="outline" data-testid="cta-final-diamond" className="h-14 px-8 border-2 border-[rgba(75,163,255,0.3)] text-[#4BA3FF] hover:bg-[rgba(75,163,255,0.1)] rounded-xl font-heading font-semibold bg-transparent transition-colors">
                <Diamond size={18} className="mr-2" /> Modo DIAMANTE
              </Button>
            </div>
          </div>
        </MagicCardFrame>
      </Section>

      {/* ── STICKY MOBILE CTA ── */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#090A0D]/95 backdrop-blur-md border-t border-white/[0.06] p-3 sm:hidden" data-testid="mobile-sticky-cta">
        <div className="flex gap-2">
          <Button onClick={handleOpenChests} data-testid="mobile-open-chests" className="flex-1 h-12 bg-[#FFC640] text-[#090A0D] font-heading font-bold hover:bg-[#FFD36B] rounded-xl transition-colors">
            <Sparkles size={16} className="mr-1" /> Abrir cofres
          </Button>
          <Button onClick={handleDiamondMode} data-testid="mobile-diamond" className="flex-1 h-12 bg-[rgba(75,163,255,0.12)] border border-[rgba(75,163,255,0.25)] text-[#4BA3FF] font-heading font-semibold rounded-xl transition-colors">
            <Diamond size={16} className="mr-1" /> Diamante
          </Button>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 sm:pb-8 border-t border-white/[0.06]" data-testid="footer">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-center sm:text-left">
            <span className="font-heading font-bold text-[#FFC640]">COFRES</span>
            <span className="font-accent font-semibold text-[#F7F2E7] ml-1">MAGICOS</span>
          </div>
          <p className="text-xs text-[#F7F2E7]/35">Sistema de rifas premium. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
